import { useEffect, useState } from 'react';
import type { CatalogResponse, CatalogSku } from '@nadoutong/contracts';
import { PLATFORM_BASE_URL } from '../config/endpoints.ts';

/**
 * Epic #898 P6 (#918): the landing page consumes the SKU market
 * (`skus[]`, additive on `CatalogResponse` since #912) instead of the
 * channel-era `models[]`/`channels[]` traversal. `fx_rate` stays on the
 * response and keeps driving the CNY conversion.
 */

/**
 * FX fallback used before the catalog resolves (and when the fetch
 * fails) — matches the pre-fetch default the page has always rendered.
 */
const FALLBACK_FX_RATE = 7.2;

interface CatalogState {
  /**
   * `null` while the request is in flight. `[]` when the request
   * failed or the payload carries no SKU market — `skus` is OPTIONAL on
   * the wire, so an old deployed platform-api omits it entirely; the
   * page renders a graceful empty state instead of crashing.
   */
  readonly skus: readonly CatalogSku[] | null;
  readonly fxRate: number;
  readonly loading: boolean;
}

export function useCatalog(): CatalogState {
  const [state, setState] = useState<CatalogState>({
    skus: null,
    fxRate: FALLBACK_FX_RATE,
    loading: true,
  });

  useEffect(() => {
    fetch(`${PLATFORM_BASE_URL}/v1/catalog?brand=nadoutong`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        const json = (await res.json()) as CatalogResponse;
        setState({
          skus: json.skus ?? [],
          fxRate: json.fx_rate,
          loading: false,
        });
      })
      .catch(() => {
        setState({ skus: [], fxRate: FALLBACK_FX_RATE, loading: false });
      });
  }, []);

  return state;
}
